import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Images,
  Pencil,
  Trophy,
  Users,
} from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import { formatEventDates, formatEventPlace } from "lib/events";

/**
 * Janela de detalhe do campeonato (rota propria, nunca modal).
 *
 * Mostra os dados da competicao e a lista de etapas — cada linha abre o
 * detalhe do evento correspondente.
 */
export default function ChampionshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tf, lang } = useI18n();

  const [item, setItem] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, list] = await Promise.all([
        api.get(`/api/championships/${id}/`),
        api.get("/api/events/", { params: { championship: id } }),
      ]);
      setItem(detail.data);
      setEvents(list.data || []);
    } catch {
      setError(t("common.loadError"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (canManage) load();
  }, [load, canManage]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="w-full animate-fade-in">
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error || t("championship.notFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/championships")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="w-full mb-6">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">{item.name}</h1>
                <p className="text-sm text-muted-foreground">
                  {[item.season, item.organizer].filter(Boolean).join(" · ") ||
                    t("championship.subtitle")}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/app/championships/${item.id}/edit`)}
            >
              <Pencil className="h-4 w-4 mr-2" />
              {t("common.edit")}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {item.description && (
            <p className="text-sm text-muted-foreground mb-4 whitespace-pre-line">
              {item.description}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              icon={CalendarDays}
              label={t("championship.events")}
              value={item.event_count}
            />
            <Stat icon={Images} label={t("event.photos")} value={item.photo_count} />
            <Stat
              icon={Users}
              label={t("championship.publicUrl")}
              value={`/championships/${item.slug}`}
              mono
            />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full">
        <CardHeader>
          <h2 className="text-sm font-semibold text-muted-foreground">
            {tf("championship.eventsCount", { count: events.length })}
          </h2>
        </CardHeader>
        <CardContent className="p-0">
          {events.length === 0 ? (
            <div className="text-center py-12">
              <CalendarDays className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("championship.noEvents")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.name")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.dates")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.place")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.photos")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.status")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr
                      key={event.id}
                      onClick={() => navigate(`/app/events/${event.id}`)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium">{event.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatEventDates(event, lang)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatEventPlace(event) || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {event.photo_count}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {t(`event.status.${event.status}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-3">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`truncate text-sm font-medium ${mono ? "font-mono" : ""}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
